import { useEffect, useRef, useState } from "react";
import { Plus, PencilSimple, Trash, AddressBook, Buildings, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { useCompany } from "../../context/CompanyContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const empty = { name: "", type: "client", email: "", phone: "", address: "", gstin: "", notes: "" };

export default function Contacts() {
  const { active, activeId } = useCompany();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [filterType, setFilterType] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const vendorFileRef = useRef(null);
  const clientFileRef = useRef(null);

  const load = async () => {
    if (!activeId) { setList([]); return; }
    const { data } = await api.get("/finance/contacts", { params: { company_id: activeId } });
    setList(data);
  };

  useEffect(() => { load(); }, [activeId]);

  const openNew = (type = "client") => { setEditing(null); setForm({ ...empty, type }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    if (!activeId) { toast.error("Select a company first"); return; }
    const payload = { ...form, company_id: activeId };
    if (editing) await api.put(`/finance/contacts/${editing.id}`, payload);
    else await api.post("/finance/contacts", payload);
    toast.success(editing ? "Updated" : "Added");
    setOpen(false); load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    await api.delete(`/finance/contacts/${id}`);
    toast.success("Deleted"); load();
  };

  const handleBulkUpload = async (type, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!activeId) { toast.error("Pick a company first"); return; }
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    setUploadResult(null);
    try {
      const { data } = await api.post(
        `/finance/contacts/upload?company_id=${activeId}&type=${type}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setUploadResult(data);
      if (data.inserted > 0) {
        toast.success(`Imported ${data.inserted} ${type}${data.inserted > 1 ? "s" : ""}` + (data.skipped_duplicates ? ` · ${data.skipped_duplicates} duplicates skipped` : ""));
      } else if (data.skipped_duplicates > 0) {
        toast.message(`All ${data.skipped_duplicates} rows already exist — nothing imported`);
      } else {
        toast.warning("No rows imported. Check that the sheet has a 'Name' column.");
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  if (!activeId) {
    return (
      <Layout title="Clients & Vendors">
        <div className="border border-black/10 p-16 text-center" data-testid="no-active-company">
          <Buildings size={36} className="mx-auto text-[#D90429]" />
          <h3 className="font-display text-2xl font-bold tracking-tight mt-4">Pick a company first</h3>
          <p className="mt-2 text-[#717680]">Contacts belong to a company. Create or select one to continue.</p>
          <button onClick={() => navigate("/finance/companies")} className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]">
            Go to Companies
          </button>
        </div>
      </Layout>
    );
  }

  const filtered = list.filter((c) => filterType === "all" ? true : c.type === filterType);

  return (
    <Layout
      title="Clients & Vendors"
      subtitle={`Inside ${active?.name || ""}`}
      actions={
        <>
          <input ref={vendorFileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="vendor-upload-input" onChange={(e) => handleBulkUpload("vendor", e)} />
          <input ref={clientFileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="client-upload-input" onChange={(e) => handleBulkUpload("client", e)} />
          <button
            onClick={() => vendorFileRef.current?.click()}
            disabled={uploading}
            data-testid="bulk-import-vendors-button"
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-black/15 text-sm font-semibold hover:bg-[#0A0A0A] hover:text-white transition-all disabled:opacity-50"
          >
            <UploadSimple size={16} weight="bold" /> {uploading ? "Importing…" : "Import Vendors"}
          </button>
          <button
            onClick={() => clientFileRef.current?.click()}
            disabled={uploading}
            data-testid="bulk-import-clients-button"
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-black/15 text-sm font-semibold hover:bg-[#0A0A0A] hover:text-white transition-all disabled:opacity-50"
          >
            <UploadSimple size={16} weight="bold" /> Import Clients
          </button>
          <button onClick={() => openNew("vendor")} data-testid="add-vendor-button" className="inline-flex items-center gap-2 px-4 py-2.5 border border-black/15 text-sm font-semibold hover:bg-[#0A0A0A] hover:text-white transition-all"><Plus size={16} weight="bold" /> Vendor</button>
          <button onClick={() => openNew("client")} data-testid="add-client-button" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] transition-all active:scale-95"><Plus size={16} weight="bold" /> Client</button>
        </>
      }
    >
      <div className="flex gap-2 mb-5">
        {["all","client","vendor"].map((t) => (
          <button key={t} onClick={() => setFilterType(t)} data-testid={`filter-${t}`} className={`px-3 py-1.5 text-xs font-mono-shelv uppercase tracking-widest border ${filterType===t ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-black/15 hover:border-[#0A0A0A]"}`}>{t}{t!=="all" && `s`} ({t==="all"? list.length : list.filter(x=>x.type===t).length})</button>
        ))}
      </div>

      {uploadResult && (
        <div className="mb-5 border-l-4 border-[#D90429] bg-[#FFE5E9]/40 px-4 py-3 text-sm flex items-start justify-between gap-4" data-testid="bulk-import-result">
          <div>
            <div className="font-bold text-[#0A0A0A]">
              Import complete · {uploadResult.inserted} {uploadResult.type}{uploadResult.inserted === 1 ? "" : "s"} added
            </div>
            <div className="text-xs text-[#4A4D57] mt-1">
              {uploadResult.skipped_duplicates > 0 && <span>{uploadResult.skipped_duplicates} duplicate name{uploadResult.skipped_duplicates === 1 ? "" : "s"} skipped · </span>}
              {uploadResult.skipped_blank > 0 && <span>{uploadResult.skipped_blank} blank row{uploadResult.skipped_blank === 1 ? "" : "s"} skipped · </span>}
              {Object.entries(uploadResult.per_sheet || {}).map(([n, v]) => `${n}: ${v}`).join(" · ") || "No per-sheet detail"}
              {uploadResult.unmatched_sheets?.length > 0 && (
                <span className="block mt-0.5 text-[#D90429]">Skipped sheets (no &lsquo;Name&rsquo; column): {uploadResult.unmatched_sheets.join(", ")}</span>
              )}
            </div>
          </div>
          <button onClick={() => setUploadResult(null)} className="text-xs text-[#717680] hover:text-[#0A0A0A]" data-testid="dismiss-import-result">Dismiss</button>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="border border-black/10 p-16 text-center text-[#717680]"><AddressBook size={36} className="mx-auto text-[#D90429]"/><p className="mt-4">No contacts yet.</p></div>
      ) : (
        <div className="border border-black/10 bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]" data-testid="contacts-table">
            <thead className="bg-[#F5F6F8] text-left"><tr>{["Name","Type","Email","Phone","GSTIN","Address",""].map(h=>(<th key={h} className="px-4 py-3 font-mono-shelv text-xs uppercase tracking-widest text-[#717680]">{h}</th>))}</tr></thead>
            <tbody>
              {filtered.map((c)=> (
                <tr key={c.id} className="border-t border-black/5 hover:bg-[#FAFBFC]" data-testid={`contact-row-${c.id}`}>
                  <td className="px-4 py-3 font-semibold">{c.name}</td>
                  <td className="px-4 py-3"><span className={`text-xs font-mono-shelv uppercase tracking-widest px-2 py-0.5 ${c.type==="client" ? "bg-[#2A9D8F]/15 text-[#2A9D8F]" : "bg-[#F4A261]/15 text-[#F4A261]"}`}>{c.type}</span></td>
                  <td className="px-4 py-3 text-[#4A4D57]">{c.email || "—"}</td>
                  <td className="px-4 py-3 text-[#4A4D57]">{c.phone || "—"}</td>
                  <td className="px-4 py-3 font-mono-shelv text-xs">{c.gstin || "—"}</td>
                  <td className="px-4 py-3 text-[#4A4D57] truncate max-w-xs">{c.address || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={()=>openEdit(c)} className="p-1.5 hover:bg-black/5" data-testid={`edit-contact-${c.id}`}><PencilSimple size={14}/></button>
                      <button onClick={()=>remove(c.id)} className="p-1.5 hover:bg-[#D90429]/10 text-[#D90429]" data-testid={`delete-contact-${c.id}`}><Trash size={14}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader><DialogTitle className="font-display tracking-tight">{editing ? "Edit contact" : `New ${form.type}`}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Name *</Label><Input data-testid="contact-name-input" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className="rounded-none"/></div>
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v)=>setForm({...form, type:v})}>
                <SelectTrigger className="rounded-none"><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="client">Client</SelectItem><SelectItem value="vendor">Vendor</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>GSTIN</Label><Input value={form.gstin} onChange={(e)=>setForm({...form, gstin:e.target.value})} className="rounded-none"/></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} className="rounded-none"/></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} className="rounded-none"/></div>
            <div className="col-span-2"><Label>Address</Label><Textarea rows={2} value={form.address} onChange={(e)=>setForm({...form, address:e.target.value})} className="rounded-none"/></div>
            <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} className="rounded-none"/></div>
          </div>
          <DialogFooter>
            <button onClick={()=>setOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
            <button onClick={save} data-testid="save-contact-button" className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]">{editing ? "Save" : "Add"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
